<template>
  <div class="modal is-active">
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Change Admin</p>
      </header>
      <section class="modal-card-body">
        <div class="field is-horizontal">
          <div class="field-body">
            <div class="field-label is-normal">
              <label class="label">Admin</label>
            </div>

            <div class="field">
              <div class="control">
                <div class="dropdown" :class="{'is-active': newAdminUserDropdownVisible}">
                  <div class="dropdown-trigger">
                    <button
                      class="button"
                      :class="{'is-loading': isLoadingMembers}"
                      :disabled="isChangingAdmin"
                      @click="toggleNewAdminUserDropdown"
                      aria-haspopup="true"
                      aria-controls="dropdown-menu">
                      <span v-if="!isLoadingMembers">
                        <span>
                          <span v-if="newAdminUser">{{ newAdminUser.email }} ({{ newAdminUser.firstName}} {{ newAdminUser.lastName }})</span>
                          <span v-if="!newAdminUser">Select</span>
                        </span>
                        <span class="icon is-small">
                          <i class="fas fa-angle-down" aria-hidden="true"></i>
                        </span>
                      </span>
                    </button>
                  </div>
                  <div class="dropdown-menu" id="dropdown-menu" role="menu">
                    <div class="dropdown-content">
                      <a v-for="member in members" :key="member.id"
                        class="dropdown-item"
                        @click="adminSelected(member)">
                        {{ member.email }} ({{ member.firstName}} {{ member.lastName }})
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <p class="help is-danger" v-if="newAdminUserError">{{ newAdminUserError }}</p>
            </div>
          </div>
        </div>

        <!-- Error notification section -->
        <div class="notification is-danger" v-if="changeAdminError">
          {{changeAdminError}}
        </div>
      </section>
      <footer class="modal-card-foot">
        <div class="container">
          <div class="is-pulled-right">
            <button
              class="button"
              @click="cancelChangeAdmin"
              :disabled="isChangingAdmin"
            >Cancel</button>
            <button
              class="button is-primary"
              @click="confirmChangeAdmin"
              :class="{'is-loading': isChangingAdmin}"
              :disabled="isChangingAdmin"
            >Change Admin</button>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import {
  LOAD_MEMBERS,
  GET_MEMBERS,
  IS_LOADING_MEMBERS,
  GET_MEMBERS_LOAD_ERROR,
  HAS_EMPTY_MEMBERS_LOAD_RESULT,
  UPDATE_ADMIN
} from "./huntAreaStore";

export default {
  name: "HuntAreaChangeAdminModal",

  props: {
    huntAreaStorePath: {
      type: String,
      required: true
    },

    huntArea: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      newAdminUserDropdownVisible: false,
      newAdminUserId: null,
      isChangingAdmin: false,
      newAdminUserError: null,
      changeAdminError: null
    };
  },

  mounted() {
    this.newAdminUserId = this.huntArea.createdBy;

    const loadMembersAction = `${this.huntAreaStorePath}/${LOAD_MEMBERS}`;
    this.$store.dispatch(loadMembersAction);
  },

  computed: {
    membersLoaded() {
      return this.huntArea != null && this.members !== null;
    },

    members() {
      const getMembers = `${this.huntAreaStorePath}/${GET_MEMBERS}`;
      return this.$store.getters[getMembers];
    },

    isLoadingMembers() {
      const isLoadingMembers = `${this.huntAreaStorePath}/${IS_LOADING_MEMBERS}`;
      return this.$store.getters[isLoadingMembers];
    },

    hasNoMembers() {
      const hasEmptyMembersLoadResult = `${this.huntAreaStorePath}/${HAS_EMPTY_MEMBERS_LOAD_RESULT}`;
      return this.$store.getters[hasEmptyMembersLoadResult];
    },

    loadMembersError() {
      const getMembersLoadError = `${this.huntAreaStorePath}/${GET_MEMBERS_LOAD_ERROR}`;
      return this.$store.getters[getMembersLoadError];
    },

    newAdminUser() {
      if (!this.newAdminUserId) {
        return null;
      }

      return this.members.find(member => member.id === this.newAdminUserId);
    }
  },

  methods: {
    toggleNewAdminUserDropdown() {
      this.newAdminUserDropdownVisible = !this.newAdminUserDropdownVisible;
    },

    adminSelected(member) {
      if (!member) {
        this.newAdminUserId = null;
        this.newAdminUserDropdownVisible = false;
        return;
      }

      this.newAdminUserId = member.id;
      this.newAdminUserDropdownVisible = false;
    },

    cancelChangeAdmin() {
      this.$emit("cancelled");
    },

    async confirmChangeAdmin() {
      if (this.newAdminUserId === null) {
        this.newAdminUserError = "Admin must be selected";
        return;
      }

      this.changeAdminError = null;
      this.isChangingAdmin = true;

      try {
        const updateAdminAction = `${this.huntAreaStorePath}/${UPDATE_ADMIN}`;
        const response = await this.$store.dispatch(updateAdminAction,
          {
            newAdminUserId: this.newAdminUserId
          });

        if (!response.success) {
          this.changeAdminError = `Change admin failed: ${response.message}`;
          return;
        }

        this.$emit("succeeded");
      } catch (error) {
        this.changeAdminError = error || 'Something went wrong.';
      } finally {
        this.isChangingAdmin = false;
      }
    }
  },

  watch: {
    newAdminUserId() {
      this.newAdminUserError = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-card {
  overflow: visible;
}

.modal-card-head {
  border-bottom: 0;
}

.modal-card-body {
  overflow: visible;

  .field-label {
    flex-grow: 0;
  }

  .control {
    flex-grow: 1;

    .dropdown {
      width: 100%;

      .dropdown-trigger {
        width: 100%;

        .button {
          width: 100%;
          justify-content: left;
        }
      }

      .dropdown-menu {
        width: 100%;

        .dropdown-content {
          max-height: calc(50vh - 40px);
          overflow-y: scroll;
        }
      }
    }
  }
}

.modal-card-foot {
  border-top: 0;
}

.notification {
  margin-top: 1em;
}
</style>
